import React, { useState } from 'react';
import { Element } from '../../../core/template.schemas';
import { useTemplateStore } from '../../hooks/useTemplateStore';
import { Label } from '@/ui-components/ui/label';
import { Input } from '@/ui-components/ui/input';
import { Button } from '@/ui-components/ui/button';
import { Checkbox } from '@/ui-components/ui/checkbox';
import { Slider } from '@/ui-components/ui/slider';
import { 
  Eye, 
  EyeOff, 
  Lock, 
  Unlock, 
  RotateCcw,
  Move,
  Palette,
  ChevronRight,
  ChevronDown,
  AlignCenterHorizontal,
  AlignCenterVertical,
  Shield
} from 'lucide-react';
import { EffectsSection } from './EffectsSection';
import { useTemplatePermissions } from '../../hooks/useTemplatePermissions';
import { cn } from '@/lib/utils/cn';

interface CommonPropertiesProps {
  element: Element;
}

/**
 * Common Properties Component
 * Displays common element properties like position, layout, appearance, etc.
 */
export const CommonProperties: React.FC<CommonPropertiesProps> = ({ element }) => {
  const updateElement = useTemplateStore(s => s.updateElement);
  const toggleElementVisibility = useTemplateStore(s => s.toggleElementVisibility);
  const { canAddWatermarks, canEditWatermarks } = useTemplatePermissions();
  
  // State for collapsible sections
  const [isSizePositionExpanded, setIsSizePositionExpanded] = useState(false);
  const template = useTemplateStore(s => s.template);

  const handlePropertyChange = (property: keyof Element, value: any) => {
    updateElement(element.id, { [property]: value });
  };

  const handlePositionChange = (axis: 'x' | 'y', value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      handlePropertyChange(axis, numValue);
    }
  };

  const handleDimensionChange = (dimension: 'width' | 'height', value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      handlePropertyChange(dimension, numValue);
    }
  };

  const handleRotationChange = (value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      handlePropertyChange('rotation', numValue);
    }
  };

  const handleCenterX = () => {
    const canvasWidth = template.canvas.width;
    const elWidth = element.width || 0;
    const centeredX = Math.round((canvasWidth - elWidth) / 2);
    updateElement(element.id, { x: centeredX });
  };

  const handleCenterY = () => {
    const canvasHeight = template.canvas.height;
    const elHeight = element.height || 0;
    const centeredY = Math.round((canvasHeight - elHeight) / 2);
    updateElement(element.id, { y: centeredY });
  };

  return (
    <>
      {/* Größe & Position Section - Collapsible */}
      <div className="space-y-3">
        <button
          onClick={() => setIsSizePositionExpanded(!isSizePositionExpanded)}
          className="flex items-center justify-between w-full text-left hover:bg-gray-50/80 p-1.5 -mx-1.5 rounded-lg transition-colors group"
        >
          <div className="flex items-center gap-2">
            <Move className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-600 transition-colors" />
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 cursor-pointer">Größe & Position</Label>
          </div>
          {isSizePositionExpanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
          )}
        </button>
        
        {isSizePositionExpanded && (
          <div className="space-y-4 pt-1">
            {/* Position Controls */}
            <div className="space-y-2">
              <Label className="text-[11px] text-gray-500 font-medium">Position</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <Input
                    type="number"
                    value={Math.round(element.x)}
                    onChange={(e) => handlePositionChange('x', e.target.value)}
                    className="h-8 text-[11px] rounded-lg border-gray-200 pr-6"
                  />
                  <span className="text-[10px] text-gray-400 absolute right-2 top-2 font-medium">X</span>
                </div>
                <div className="relative">
                  <Input
                    type="number"
                    value={Math.round(element.y)}
                    onChange={(e) => handlePositionChange('y', e.target.value)}
                    className="h-8 text-[11px] rounded-lg border-gray-200 pr-6"
                  />
                  <span className="text-[10px] text-gray-400 absolute right-2 top-2 font-medium">Y</span>
                </div>
              </div>
            </div>

            {/* Alignment Controls */}
            <div className="space-y-2">
              <Label className="text-[11px] text-gray-500 font-medium">Ausrichten</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCenterX}
                  title="Horizontal zentrieren"
                  className="h-8 text-[11px] rounded-lg border-gray-200 gap-2 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-all"
                >
                  <AlignCenterHorizontal className="w-3.5 h-3.5" />
                  <span>Horizontal</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCenterY}
                  title="Vertikal zentrieren"
                  className="h-8 text-[11px] rounded-lg border-gray-200 gap-2 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-all"
                >
                  <AlignCenterVertical className="w-3.5 h-3.5" />
                  <span>Vertikal</span>
                </Button>
              </div>
            </div>

            {/* Layout/Size Controls */}
            {(element.width !== undefined || element.height !== undefined) && (
              <div className="space-y-2">
                <Label className="text-[11px] text-gray-500 font-medium">Größe</Label>
                <div className="grid grid-cols-2 gap-3">
                  {element.width !== undefined && (
                    <div className="relative">
                      <Input
                        type="number"
                        value={Math.round(element.width)}
                        onChange={(e) => handleDimensionChange('width', e.target.value)}
                        className="h-8 text-[11px] rounded-lg border-gray-200 pr-6"
                      />
                      <span className="text-[10px] text-gray-400 absolute right-2 top-2 font-medium">W</span>
                    </div>
                  )}
                  {element.height !== undefined && (
                    <div className="relative">
                      <Input
                        type="number"
                        value={Math.round(element.height)}
                        onChange={(e) => handleDimensionChange('height', e.target.value)}
                        className="h-8 text-[11px] rounded-lg border-gray-200 pr-6"
                      />
                      <span className="text-[10px] text-gray-400 absolute right-2 top-2 font-medium">H</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Rotation */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[11px] text-gray-500 font-medium">Rotation</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePropertyChange('rotation', 0)}
                  className="h-5 w-5 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                >
                  <RotateCcw className="w-3 h-3" />
                </Button>
              </div>
              <div className="relative">
                <Input
                  type="number"
                  value={Math.round(element.rotation)}
                  onChange={(e) => handleRotationChange(e.target.value)}
                  className="h-8 text-[11px] rounded-lg border-gray-200 pr-6"
                />
                <span className="text-[10px] text-gray-400 absolute right-2 top-2 font-medium">°</span>
              </div>
            </div>

            {/* Layer Order */}
            <div className="space-y-2">
              <Label className="text-[11px] text-gray-500 font-medium">Ebene (Z-Index)</Label>
              <Input
                type="number"
                value={element.zIndex}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (!isNaN(value)) {
                    handlePropertyChange('zIndex', value);
                  }
                }}
                className="h-8 text-[11px] rounded-lg border-gray-200"
              />
            </div>
          </div>
        )}
      </div>

      {/* Appearance Section */}
      <div className="space-y-4 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <Palette className="w-3.5 h-3.5 text-gray-500" />
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Erscheinung</Label>
        </div>
        
        {/* Opacity */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <Label className="text-[11px] text-gray-500 font-medium">Deckkraft</Label>
            <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-md font-bold">
              {Math.round(element.opacity * 100)}%
            </span>
          </div>
          <Slider
            value={[element.opacity]}
            onValueChange={([value]) => handlePropertyChange('opacity', value)}
            max={1}
            min={0}
            step={0.01}
            className="w-full"
          />
        </div>

        {/* Visibility & Lock */}
        <div className="grid grid-cols-2 gap-2">
          <div 
            className="flex items-center justify-between p-2 rounded-xl border border-gray-100 bg-gray-50/30 hover:bg-white hover:border-gray-200 hover:shadow-sm transition-all cursor-pointer group"
            onClick={() => toggleElementVisibility(element.id)}
          >
            <div className="flex items-center gap-2">
              {element.visible ? (
                <Eye className="w-3.5 h-3.5 text-blue-500" />
              ) : (
                <EyeOff className="w-3.5 h-3.5 text-gray-400" />
              )}
              <Label className="text-[11px] text-gray-600 font-medium cursor-pointer">Sichtbar</Label>
            </div>
            <Checkbox
              checked={element.visible}
              onClick={(e) => e.stopPropagation()}
              onCheckedChange={(checked) => handlePropertyChange('visible', checked === true)}
              className="h-4 w-4 rounded-md border-gray-300 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
            />
          </div>

          <div 
            className="flex items-center justify-between p-2 rounded-xl border border-gray-100 bg-gray-50/30 hover:bg-white hover:border-gray-200 hover:shadow-sm transition-all cursor-pointer group"
            onClick={() => handlePropertyChange('locked', !element.locked)}
          >
            <div className="flex items-center gap-2">
              {element.locked ? (
                <Lock className="w-3.5 h-3.5 text-orange-500" />
              ) : (
                <Unlock className="w-3.5 h-3.5 text-gray-400" />
              )}
              <Label className="text-[11px] text-gray-600 font-medium cursor-pointer">Gesperrt</Label>
            </div>
            <Checkbox
              checked={element.locked}
              onClick={(e) => e.stopPropagation()}
              onCheckedChange={(checked) => handlePropertyChange('locked', checked === true)}
              className="h-4 w-4 rounded-md border-gray-300 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
            />
          </div>
        </div>
        
        {/*
          Watermark Toggle - Only for authorized users
        */}
        {((element.isWatermark && canEditWatermarks) || (!element.isWatermark && canAddWatermarks)) && (
          <div 
            className="flex items-center justify-between p-2 rounded-xl border border-gray-100 bg-gray-50/30 hover:bg-white hover:border-gray-200 hover:shadow-sm transition-all cursor-pointer group"
            onClick={() => handlePropertyChange('isWatermark', !element.isWatermark)}
          >
            <div className="flex items-center gap-2">
              <Shield className={cn("w-3.5 h-3.5 transition-colors", element.isWatermark ? "text-purple-500" : "text-gray-400")} />
              <Label className="text-[11px] text-gray-600 font-medium cursor-pointer">Als Wasserzeichen</Label>
            </div>
            <Checkbox
              checked={element.isWatermark || false}
              onClick={(e) => e.stopPropagation()}
              onCheckedChange={(checked) => handlePropertyChange('isWatermark', checked === true)}
              className="h-4 w-4 rounded-md border-gray-300 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
            />
          </div>
        )}
      </div>

      {/* Effects Section */}
      <EffectsSection element={element} />

      {/* Element Name */}
      <div className="space-y-3 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-3.5 h-3.5 border-2 border-gray-400 rounded-md flex items-center justify-center">
            <span className="text-[9px] font-bold text-gray-500">T</span>
          </div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Element-Name</Label>
        </div>
        
        <Input
          type="text"
          value={element.name || ''}
          onChange={(e) => handlePropertyChange('name', e.target.value)}
          placeholder="z.B. Spieler-Name"
          className="h-8 text-[11px] rounded-lg border-gray-200 bg-white"
        />
      </div>
    </>
  );
};
